import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Clock, Plus, Search, DollarSign, User, Lock, CheckCircle2, AlertCircle, X, Calendar, Wallet } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function ShiftsPage() {
  const [search, setSearch] = useState('')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [openingCash, setOpeningCash] = useState('500000')

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/pos/shifts', { search }],
    queryFn: () => api.get('/pos/shifts', { params: { search } }).then(r => r.data),
  })

  const openShiftMutation = useMutation({
    mutationFn: (cash: number) => api.post('/pos/shifts', { opening_cash: cash, cashier_name: 'مدیر فروشگاه' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/pos/shifts'] })
      toast.success('شیفت کاری جدید با موفقیت باز شد')
      setIsOpenModal(false)
    },
    onError: () => toast.error('خطا در باز کردن شیفت')
  })

  const items = data?.items || [
    {
      id: 'sh_1',
      shift_number: 'SH-2026-0801',
      cashier_name: 'مدیر فروشگاه',
      status: 'OPEN',
      opening_cash: 500000,
      total_sales: 98100000,
      opened_at: '2026-08-01T08:00:00Z',
      closed_at: null
    },
    {
      id: 'sh_0',
      shift_number: 'SH-2026-0731',
      cashier_name: 'رضا کریمی',
      status: 'CLOSED',
      opening_cash: 300000,
      total_sales: 45200000,
      opened_at: '2026-07-31T08:00:00Z',
      closed_at: '2026-07-31T18:00:00Z'
    }
  ]

  const activeShift = items.find((s: any) => s.status === 'OPEN')

  return (
    <div className="p-4 space-y-4">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <Clock className="w-6 h-6 text-primary-500" />
            مدیریت شیفت‌های صندوق
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            کنترل باز/بسته شدن صندوق، موجودی اولیه و گزارش فروش هر شیفت
          </p>
        </div>
        <Button onClick={() => setIsOpenModal(true)} disabled={!!activeShift}>
          <Plus className="w-4 h-4 ml-1" />
          شروع شیفت جدید
        </Button>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/20 dark:to-neutral-900 border-emerald-200 dark:border-emerald-800/40">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">وضعیت شیفت فعلی</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white mt-0.5">
              {activeShift ? `فعال (${activeShift.cashier_name})` : 'هیچ شیفتی باز نیست'}
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <Wallet className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">موجودی اولیه صندوق فعلی</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white mt-0.5">
              {activeShift ? `${Number(activeShift.opening_cash || 0).toLocaleString()} تومان` : '---'}
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">فروش شیفت جاری</p>
            <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
              {activeShift ? `${Number(activeShift.total_sales || 0).toLocaleString()} تومان` : '---'}
            </p>
          </div>
        </div>
      </div>

      {/* Filter / Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="جستجوی شیفت..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-6 w-48" /></div>)}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Clock className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">شیفتی یافت نشد</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">کد شیفت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">صندوق‌دار</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">وضعیت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">موجودی اولیه</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">مجموع فروش</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">زمان شروع</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">زمان پایان</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Clock className="w-4 h-4 text-neutral-400" />
                      {item.shift_number || item.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-800 dark:text-neutral-200">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-neutral-400" />
                        {item.cashier_name || 'صندوق‌دار'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant={item.status === 'OPEN' ? 'success' : 'neutral'}>
                        {item.status === 'OPEN' ? 'باز (در حال کار)' : 'بسته شده'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                      {Number(item.opening_cash || 0).toLocaleString()} تومان
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {Number(item.total_sales || 0).toLocaleString()} تومان
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {item.opened_at ? new Date(item.opened_at).toLocaleDateString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : '---'}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {item.closed_at ? new Date(item.closed_at).toLocaleDateString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : 'در حال انجام'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Open Shift */}
      <AnimatePresence>
        {isOpenModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary-500" />
                  شروع شیفت کاری جدید
                </h3>
                <button onClick={() => setIsOpenModal(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  openShiftMutation.mutate(Number(openingCash) || 0)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    موجودی نقد اولیه در صندوق (تومان)
                  </label>
                  <Input
                    type="number"
                    value={openingCash}
                    onChange={(e) => setOpeningCash(e.target.value)}
                    placeholder="500000"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpenModal(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={openShiftMutation.isPending}>
                    باز کردن شیفت
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
